/**
 * @file services/roles.ts
 * @description 角色管理 API 封裝（Task 11）。
 *
 * 提供 TanStack Query hooks 與 mutation helper，覆蓋：
 * - GET  /api/settings/roles         → useRoleList
 * - GET  /api/settings/roles/:code   → useRoleDetail
 * - POST /api/settings/roles         → useCreateRole
 * - PATCH /api/settings/roles/:code  → useUpdateRole
 * - PUT  /api/settings/roles/:code/capabilities → usePutCapabilities
 * - PUT  /api/settings/roles/:code/menu-hidden  → usePutMenuHidden
 * - DELETE /api/settings/roles/:code → useDeleteRole
 *
 * 所有錯誤訊息由後端提供繁中文案，直接使用 err.message 展示即可。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, ApiError } from '@/lib/api-client';
import { message } from '@/lib/antd-app';
import type { CreateRoleInput, UpdateRoleInput } from '@zhcb/shared';

// ─── DTO 型別 ────────────────────────────────────────────────────────────────

/**
 * 角色列表項（GET /api/settings/roles 回傳的每個 item）。
 */
export interface RoleListItem {
  /** 角色代碼（唯一識別符，英數字底線連字號） */
  code: string;
  /** 顯示名稱 */
  name: string;
  /** 描述 */
  description: string | null;
  /** 是否為內置角色（不可刪除） */
  isBuiltin: boolean;
  /** 工作台類型（決定佈局殼與菜單基準樹） */
  workbenchType: string;
  /** 狀態：active | disabled */
  status: 'active' | 'disabled';
  /** 使用此角色的用戶數 */
  userCount: number;
}

/**
 * 角色詳情（GET /api/settings/roles/:code 回傳的 item）。
 * 在列表項基礎上額外帶能力集與菜單黑名單。
 */
export interface RoleDetail extends RoleListItem {
  /** 該角色擁有的能力鍵陣列 */
  capabilities: string[];
  /** 菜單可見性黑名單（強制隱藏的菜單 key 陣列） */
  menuHidden: string[];
}

/** GET /api/settings/roles 回傳結構 */
interface RoleListResponse {
  items: RoleListItem[];
}

/** GET /api/settings/roles/:code 回傳結構 */
interface RoleDetailResponse {
  item: RoleDetail;
}

// ─── Query Key 工廠 ──────────────────────────────────────────────────────────

export const roleQueryKeys = {
  /** 列表 queryKey */
  list: () => ['settings-roles'] as const,
  /** 詳情 queryKey（按代碼） */
  detail: (code: string) => ['settings-roles', code] as const,
};

// ─── Query Hooks ─────────────────────────────────────────────────────────────

/**
 * 取得角色列表。
 *
 * @returns useQuery 結果，data.items 為角色列表
 */
export function useRoleList() {
  return useQuery({
    queryKey: roleQueryKeys.list(),
    queryFn: () => apiClient.get<RoleListResponse>('/api/settings/roles'),
  });
}

/**
 * 取得角色詳情（含能力集與菜單黑名單）。
 *
 * @param code 角色代碼
 * @returns useQuery 結果，data.item 為角色詳情
 */
export function useRoleDetail(code: string) {
  return useQuery({
    queryKey: roleQueryKeys.detail(code),
    queryFn: () => apiClient.get<RoleDetailResponse>(`/api/settings/roles/${code}`),
    enabled: !!code,
  });
}

// ─── Mutation Hooks ──────────────────────────────────────────────────────────

/**
 * 新建角色。
 *
 * 成功後 invalidate 列表 queryKey，自動刷新表格。
 * 錯誤訊息直接取後端 message 展示給使用者。
 */
export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRoleInput) =>
      apiClient.post<{ item: RoleDetail }>('/api/settings/roles', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: roleQueryKeys.list() });
      void message.success('角色建立成功');
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : '新建角色失敗';
      void message.error(msg);
    },
  });
}

/**
 * 更新角色基本信息（PATCH）。
 *
 * 成功後同時 invalidate 列表與詳情 queryKey。
 */
export function useUpdateRole(code: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateRoleInput) =>
      apiClient.patch<{ item: RoleDetail }>(`/api/settings/roles/${code}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: roleQueryKeys.list() });
      void qc.invalidateQueries({ queryKey: roleQueryKeys.detail(code) });
      void message.success('已儲存角色基本信息');
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : '更新角色失敗';
      void message.error(msg);
    },
  });
}

/**
 * 全量替換角色能力集（PUT capabilities）。
 *
 * 成功後 invalidate 詳情 queryKey，重新載入最新能力集。
 */
export function usePutCapabilities(code: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (capabilities: string[]) =>
      apiClient.put<{ item: RoleDetail }>(`/api/settings/roles/${code}/capabilities`, {
        capabilities,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: roleQueryKeys.detail(code) });
      void qc.invalidateQueries({ queryKey: roleQueryKeys.list() });
      void message.success('功能權限已儲存');
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : '儲存功能權限失敗';
      void message.error(msg);
    },
  });
}

/**
 * 全量替換菜單可見性黑名單（PUT menu-hidden）。
 *
 * 成功後 invalidate 詳情 queryKey，重新載入最新黑名單。
 */
export function usePutMenuHidden(code: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (menuKeys: string[]) =>
      apiClient.put<{ item: RoleDetail }>(`/api/settings/roles/${code}/menu-hidden`, {
        menuKeys,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: roleQueryKeys.detail(code) });
      void message.success('菜單可見性已儲存');
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : '儲存菜單可見性失敗';
      void message.error(msg);
    },
  });
}

/**
 * 刪除角色（DELETE）。
 *
 * 成功後 invalidate 列表 queryKey，刷新表格。
 * 內置角色 / 仍有用戶使用 / admin 角色等錯誤由後端返回，
 * 直接以 err.message 展示給使用者。
 */
export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => apiClient.delete<{ ok: true }>(`/api/settings/roles/${code}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: roleQueryKeys.list() });
      void message.success('角色已刪除');
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : '刪除角色失敗';
      void message.error(msg);
    },
  });
}
